import React, { ReactElement, ReactNode } from "react";
import Head from "next/head";
import { useModal } from "@hooks/useModalContext";
import dynamic from "next/dynamic";
import Mobile from "@components/shared/blogPost/sideWidget/mobile";
import { useDomainContext } from "@hooks/useDomainContext";
import {
    generateGlobalVariables,
    minifyCSS,
    getFinalFontFamily,
} from "./utils";
import CcpaPrivacyNotice from "@components/shared/legal/ccpaPrivacyNotice";
import styles from "./styles.module.scss";
import PreHeader from "@components/shared/header/preHeader";
import { RouteConfig } from "./ts/types";

const Header = dynamic(() => import("@components/shared/header"), {
    loading: () => <p>Loading...</p>,
});
const HeaderTemplate2 = dynamic(
    () => import("@page-builder/components/headerTemplate2"),
    {
        loading: () => <p>Loading...</p>,
    },
);
const HeaderTemplate3 = dynamic(
    () => import("@page-builder/components/headerTemplate3"),
    {
        loading: () => <p>Loading...</p>,
    },
);
const Modal = dynamic(() => import("@components/shared/modal"));
const Footer = dynamic(() => import("@components/shared/footer"));
const FooterTemplate2 = dynamic(
    () => import("@page-builder/components/footerTemplate2"),
);
const FooterTemplate3 = dynamic(
    () => import("@page-builder/components/footerTemplate3"),
);
const CloseIcon = dynamic(() => import("@components/svg/closeIcon"));
const TermsAndConditions = dynamic(
    () => import("@components/shared/legal/termsAndConditions"),
);
const PrivacyPolicy = dynamic(
    () => import("@components/shared/legal/privacyPolicy"),
);

export const availableFonts: { [key: string]: string[] } = {
    Roboto: ["Light", "Regular", "Medium", "Bold", "Black"],
    OpenSans: ["Light", "Regular", "Medium", "SemiBold", "Bold", "Black"],
    Lato: ["Light", "Regular", "Bold", "Black"],
    Poppins: ["Light", "Regular", "Medium", "SemiBold", "Bold", "Black"],
    Hind: ["Light", "Regular", "Medium", "SemiBold", "Bold"],
    Cairo: ["Light", "Regular", "Medium", "SemiBold", "Bold", "Black"],
    Almarai: ["Light", "Regular", "Bold"],
    Oxygen: ["Light", "Regular", "Bold"],
    Montserrat: ["Light", "Regular", "Medium", "Bold", "Black"],
    Manrope: [
        "ExtraLight",
        "Light",
        "Regular",
        "Medium",
        "SemiBold",
        "Bold",
        "ExtraBold",
    ],
    Inter: ["Light", "Regular", "Medium", "SemiBold", "Bold", "Black"],
    PTSans: ["Regular", "Bold"],
    PostGrotesk: ["Regular", "Medium", "Bold"],
    DMSans: ["Regular", "Medium", "Bold"],
    CircularStd: ["Regular", "Bold"],
    BeVietnamPro: ["Regular", "Bold"],
};

export const weightsMap: { [key: string]: string } = {
    ExtraLight: "200",
    Light: "300",
    Regular: "400",
    Medium: "500",
    SemiBold: "600",
    Bold: "700",
    ExtraBold: "800",
    Black: "900",
};

export const weightsMapV2: { [key: string]: string } = {
    "200": "ExtraLight",
    "300": "Light",
    "400": "Regular",
    "500": "Medium",
    "600": "SemiBold",
    "700": "Bold",
    "800": "ExtraBold",
    "900": "Black",
};

export default function CommonWrapper({
    children,
    fontFamily = "Poppins",
    header,
    footer,
}: {
    children: ReactNode;
    fontFamily?: string;
    header?: ReactNode;
    footer?: ReactNode;
}): ReactElement {
    const { modal, closeModal } = useModal();

    const { routeConfig, domain, domainNameTranslation, locale } =
        useDomainContext();

    const templateGlobalConfig = routeConfig?.templateConfig;

    const handleClose = () => {
        closeModal();
    };

    const { active: modalActive, content: modalContent } = modal;

    const getModalContent = () => {
        switch (modalContent) {
            case "privacy":
                return <PrivacyPolicy />;
            case "terms":
                return <TermsAndConditions />;
            case "sideWidget":
                return <Mobile />;
            case "ccpaPrivacyNotice":
                return <CcpaPrivacyNotice />;
            default:
                return null;
        }
    };

    const getHeader = () => {
        if (header) {
            return header;
        } else {
            const headerFromPageBuilder =
                templateGlobalConfig?.headerAndFooter?.header?.headerTemplate;
            switch (headerFromPageBuilder) {
                case "template-1":
                    return <Header />;
                case "template-2":
                    return <HeaderTemplate2 />;
                case "template-3":
                    return <HeaderTemplate3 />;
                default:
                    return <Header />;
            }
        }
    };
    const getFooter = () => {
        if (footer) {
            return footer;
        } else {
            const footerFromPageBuilder =
                templateGlobalConfig?.headerAndFooter?.footer?.footerTemplate;
            switch (footerFromPageBuilder) {
                case "template-1":
                    return <Footer />;
                case "template-2":
                    return <FooterTemplate2 />;
                case "template-3":
                    return <FooterTemplate3 />;
                default:
                    return <Footer />;
            }
        }
    };

    const metatags = routeConfig?.config?.seo?.meta;

    const finalFontFamily = getFinalFontFamily(
        routeConfig as RouteConfig,
        locale,
        fontFamily,
    );

    const getFinalFontWeights = () => {
        const fontWeightsFromOtherLanguages =
            templateGlobalConfig?.variantConfig?.otherLanguagesConfig?.[locale]
                ?.fontWeights;

        const fontWeightsFromMainLanguage =
            templateGlobalConfig?.theme?.fontWeights;

        const fontWeights =
            fontWeightsFromOtherLanguages || fontWeightsFromMainLanguage;

        if (fontWeights && fontWeights.length) {
            return fontWeights;
        } else {
            return availableFonts[finalFontFamily].map(
                (weight) => weightsMap[weight],
            );
        }
    };

    const finalFontWeights = getFinalFontWeights();

    const domainCustomCss =
        typeof templateGlobalConfig?.css === "string"
            ? minifyCSS(templateGlobalConfig?.css)
            : "";

    const preHeaderTextBlock = domain?.textBlocks?.find(
        (block) => block.position === "pre-header",
    );

    const pageCustomCss = minifyCSS(routeConfig?.config?.css ?? "");
    const preHeaderText =
        preHeaderTextBlock?.text ||
        routeConfig?.config?.settings?.preHeaderText;

    const globalVariables = templateGlobalConfig
        ? generateGlobalVariables(templateGlobalConfig)
        : {};

    const remappedThemeVariables = {
        "--primary-darken-1": "var(--global-theme-shades-darken1)",
        "--primary-darken-2": "var(--global-theme-shades-darken2)",
        "--primary-darken-3": "var(--global-theme-shades-darken3)",
        "--primary-darken-4": "var(--global-theme-shades-darken4)",
        "--primary-darken-5": "var(--global-theme-shades-darken5)",
        "--primary": "var(--global-theme-shades-primary)",
        "--primary-lighten-1": "var(--global-theme-shades-lighten1)",
        "--primary-lighten-2": "var(--global-theme-shades-lighten2)",
        "--primary-lighten-3": "var(--global-theme-shades-lighten3)",
        "--primary-lighten-4": "var(--global-theme-shades-lighten4)",
        "--primary-lighten-5": "var(--global-theme-shades-lighten5)",
    };

    const modalTitles: Record<string, string> = {
        privacy:
            domain?.translations?.[`${domainNameTranslation}_PrivacyPolicy`] ??
            "Privacy Policy",
        terms:
            domain?.translations?.[
                `${domainNameTranslation}_TermsConditions`
            ] ?? "Terms and Conditions",
        sideWidget: "Contents",
        ccpaPrivacyNotice: "CCPA Privacy Notice",
    };

    return (
        <>
            <Head>
                <>
                    {finalFontWeights.map((weight: string) => (
                        <link
                            key={`font-preload-${weight}`}
                            rel="preload"
                            as="font"
                            type="font/woff2"
                            href={`/assets/fonts/${finalFontFamily}/${finalFontFamily}-${weightsMapV2[weight]}.woff2`}
                            crossOrigin="anonymous"
                        />
                    ))}
                    <style
                        dangerouslySetInnerHTML={{
                            __html: minifyCSS(
                                finalFontWeights
                                    .map(
                                        (
                                            weight: string,
                                        ): string => `@font-face {
                                    font-family: '${finalFontFamily}';
                                    src: url('/assets/fonts/${finalFontFamily}/${finalFontFamily}-${weightsMapV2[weight]}.woff2') format('truetype');
                                    font-weight: ${weight};
                                    font-style: normal;
                                    font-display: swap;
                                }`,
                                    )
                                    .join(" "),
                            ),
                        }}
                    />
                </>
                {metatags &&
                    metatags.length &&
                    metatags.map((item, index) => (
                        <meta
                            name={item.key}
                            content={item.value}
                            key={index}
                        />
                    ))}

                {domainCustomCss ? (
                    <>
                        <style
                            dangerouslySetInnerHTML={{
                                __html: domainCustomCss,
                            }}
                        />
                    </>
                ) : null}
                {pageCustomCss ? (
                    <>
                        <style
                            dangerouslySetInnerHTML={{
                                __html: pageCustomCss,
                            }}
                        />
                    </>
                ) : null}
            </Head>

            <div
                style={{
                    fontFamily: `'${finalFontFamily}', sans-serif`,
                    ...globalVariables,
                    ...remappedThemeVariables,
                }}
            >
                {preHeaderText && <PreHeader text={preHeaderText} />}

                {getHeader()}
                <main
                    className={`${styles.wrapper} max-w-7xl mx-auto block px-4vw lg:px-4`}
                >
                    {children}
                </main>
                {getFooter()}
                <Modal
                    onClose={handleClose}
                    active={modalActive}
                    closeIcon={<CloseIcon />}
                    backdropClosable
                    modalStyles={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight:
                            modalContent === "sideWidget" ? "80vh" : "100%",
                    }}
                    modalClassName="h-[90vh] rounded"
                    bodyStyles={{ overflowY: "scroll" }}
                    width={900}
                    header={modalTitles[modalContent ?? ""]}
                    activeCloseOnEscButton
                >
                    {getModalContent()}
                </Modal>
            </div>
        </>
    );
}
